import axiosInstance from '@/api/axiosInstance';
import { buildQueryString } from '@/utils/query';
import { ExportFormat } from '@/constants/export-dropdown.constants';
import {
  Department, DepartmentCreate, DepartmentUpdate, DepartmentFilterParams, PaginatedDepartmentsResponse,
  DepartmentCustomFieldDefinition, DepartmentCustomFieldDefinitionFilterParams, PaginatedDepartmentCustomFieldDefinitionsResponse,
  DepartmentCfnCreateDto, DepartmentCfnUpdateDto,
} from '@/types/department.types';
import { DEPARTMENT_ENDPOINTS } from '@/api/endpoints/department.endpoints';
import { ManagementApiService, FormApiService } from '@/types/management.types';

export const DepartmentManagementApiService: ManagementApiService<Department, DepartmentFilterParams> = {
  getAll: async (offset = 0, limit = 20, filters?: DepartmentFilterParams): Promise<PaginatedDepartmentsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedDepartmentsResponse>(`${DEPARTMENT_ENDPOINTS.DEPARTMENTS}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(DEPARTMENT_ENDPOINTS.DEPARTMENT_DETAIL(id));
  },
  exportItems: async (format: ExportFormat, filters?: Omit<DepartmentFilterParams, 'offset' | 'limit'>): Promise<Blob> => {
    const query = buildQueryString({ ...filters, format: format.toLowerCase() });
    return (await axiosInstance.get<Blob>(`${DEPARTMENT_ENDPOINTS.DEPARTMENTS_EXPORT}${query}`, { responseType: 'blob' })).data;
  },
};

export const DepartmentFormApiService: FormApiService<Department, DepartmentCreate, DepartmentUpdate> & {
  createItemForTenant: (data: DepartmentCreate, tenantId?: number | null) => Promise<Department>;
} = {
  // Edit-form fetch: keep `{en}…{ar}…` multilang tags (skipMultilangStrip) so
  // the per-language name editor round-trips every translation — a stripped
  // fetch would show only English and saving would delete the others.
  getItem: async (id: number | string): Promise<Department> =>
    (await axiosInstance.get<Department>(
      `${DEPARTMENT_ENDPOINTS.DEPARTMENT_DETAIL(id)}?expand=head,parent,custom_field_values`,
      { skipMultilangStrip: true } as object,
    )).data,
  createItem: async (data: DepartmentCreate): Promise<Department> => {
    const { custom_field_values, ...baseData } = data;
    const created = (await axiosInstance.post<Department>(DEPARTMENT_ENDPOINTS.DEPARTMENTS, baseData)).data;
    if (custom_field_values && custom_field_values.length > 0) {
      return (await axiosInstance.put<Department>(DEPARTMENT_ENDPOINTS.DEPARTMENT_DETAIL(created.id), { ...baseData, custom_field_values })).data;
    }
    return created;
  },
  createItemForTenant: async (data: DepartmentCreate, tenantId?: number | null): Promise<Department> => {
    const { custom_field_values, ...baseData } = data;
    const query = tenantId ? `?tenant_id=${tenantId}` : '';
    const created = (await axiosInstance.post<Department>(`${DEPARTMENT_ENDPOINTS.DEPARTMENTS}${query}`, baseData)).data;
    if (custom_field_values && custom_field_values.length > 0) {
      return (await axiosInstance.put<Department>(DEPARTMENT_ENDPOINTS.DEPARTMENT_DETAIL(created.id), { ...baseData, custom_field_values })).data;
    }
    return created;
  },
  updateItem: async (id: number | string, data: DepartmentUpdate): Promise<Department> =>
    (await axiosInstance.put<Department>(DEPARTMENT_ENDPOINTS.DEPARTMENT_DETAIL(id), data)).data,
};

export const DepartmentCfnManagementApiService: ManagementApiService<DepartmentCustomFieldDefinition, { name?: string }> = {
  getAll: async (offset = 0, limit = 10, filters?: { name?: string }): Promise<PaginatedDepartmentCustomFieldDefinitionsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedDepartmentCustomFieldDefinitionsResponse>(`${DEPARTMENT_ENDPOINTS.DEPARTMENTS_CUSTOM_FIELDS}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(DEPARTMENT_ENDPOINTS.DEPARTMENT_CUSTOM_FIELD_DETAIL(id));
  },
};

const parseConfig = (config: unknown): object | null => {
  if (typeof config === 'string') {
    try { return JSON.parse(config); } catch { return config === '{}' ? {} : null; }
  }
  if (typeof config === 'object' && config !== null) return config as object;
  return null;
};

export const DepartmentCfnFormApiService: FormApiService<DepartmentCustomFieldDefinition, DepartmentCfnCreateDto, DepartmentCfnUpdateDto> = {
  createItem: async (data: DepartmentCfnCreateDto): Promise<DepartmentCustomFieldDefinition> =>
    (await axiosInstance.post<DepartmentCustomFieldDefinition>(DEPARTMENT_ENDPOINTS.DEPARTMENTS_CUSTOM_FIELDS, { ...data, config: parseConfig(data.config) })).data,
  updateItem: async (id: number, data: DepartmentCfnUpdateDto): Promise<DepartmentCustomFieldDefinition> =>
    (await axiosInstance.put<DepartmentCustomFieldDefinition>(DEPARTMENT_ENDPOINTS.DEPARTMENT_CUSTOM_FIELD_DETAIL(id), { ...data, config: parseConfig(data.config) })).data,
};

export const DepartmentsApi = {
  Management: DepartmentManagementApiService,
  Form: DepartmentFormApiService,
  CfnManagement: DepartmentCfnManagementApiService,
  CfnForm: DepartmentCfnFormApiService,
  // backward compatibility
  getDepartments: DepartmentManagementApiService.getAll,
  getDepartmentById: DepartmentFormApiService.getItem!,
  createDepartment: DepartmentFormApiService.createItem,
  updateDepartment: DepartmentFormApiService.updateItem,
  deleteDepartment: DepartmentManagementApiService.deleteItem,
  exportDepartments: DepartmentManagementApiService.exportItems,
  getDepartmentCustomFieldDefinitions: async (params?: DepartmentCustomFieldDefinitionFilterParams): Promise<PaginatedDepartmentCustomFieldDefinitionsResponse> => {
    const query = buildQueryString({ offset: 0, limit: 100, ...params });
    return (await axiosInstance.get<PaginatedDepartmentCustomFieldDefinitionsResponse>(`${DEPARTMENT_ENDPOINTS.DEPARTMENTS_CUSTOM_FIELDS}${query}`)).data;
  },
};